import { createI18n } from 'vue-i18n';
import { Settings as LuxonSettings } from 'luxon';
import axios from 'axios';

const messageModules = import.meta.glob('../locale/*/LC_MESSAGES/messages.po');

type Language = 'it' | 'en';

export function detectLang<L extends string>(languages: L[], defaultLang: L): L {
  let urlLanguage = null;
  if (window) {
    const url = new URL(window.location.href);
    urlLanguage = url.searchParams.get('lang');
  }

  if (urlLanguage && languages.includes(urlLanguage as any)) {
    return urlLanguage as L;
  }

  for (const language of navigator.languages) {
    const lang = language.substring(0, 2);
    if (languages.includes(lang as any)) {
      return lang as L;
    }
  }
  // default
  return defaultLang;
}

function langToLocale(lang: Language) {
  if (lang === 'en') {
    return 'en_GB';
  }
  return null;
}

const i18n = createI18n({
  legacy: false,
  locale: '',
  fallbackLocale: 'it',
  fallbackFormat: true,
  fallbackWarn: true,
  missingWarn: false,
});

const loadedLanguages = new Set<string>();

function setI18nLanguage(lang: Language) {
  if (lang === i18n.global.locale.value) {
    return;
  }

  LuxonSettings.defaultLocale = lang;
  axios.defaults.headers.common['Accept-Language'] = lang;
  document.documentElement.setAttribute('lang', lang);

  (async () => {
    const fallback = lang === 'it' ? 'it' : 'en';

    if (!loadedLanguages.has(lang)) {
      let locale = langToLocale(lang);
      if (locale !== null) {
        const messages: any = await messageModules[`../locale/${locale}/LC_MESSAGES/messages.po`]?.();
        i18n.global.setLocaleMessage(lang, messages.default);
      }

      locale = langToLocale(fallback);
      if (locale !== null) {
        const messages: any = await messageModules[`../locale/${locale}/LC_MESSAGES/messages.po`]?.();
        i18n.global.setLocaleMessage(fallback, messages.default);
      }
      loadedLanguages.add(lang);
    }

    i18n.global.locale.value = lang;
    i18n.global.fallbackLocale.value = fallback;
    i18n.global.missingWarn = lang !== 'it';
  })();
}

setI18nLanguage(detectLang(['it', 'en'], 'en'));
if (typeof window !== 'undefined') {
  window.addEventListener('languagechange', () => setI18nLanguage(detectLang(['it', 'en'], 'en')));
}

export const $t = i18n.global.t;
export const $tm = i18n.global.tm;

export default i18n;
